import { useState } from "react";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Button, Card, CardMedia, Chip, Stack, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";

import awsLogo from "../../../../assets/aws-logo.svg";
import awsLogoDark from "../../../../assets/aws-logo-dark-mode.svg";
import azureLogo from "../../../../assets/azure-logo.svg";
import Fsk8sPreviewDark from "../../../../assets/fsk8s/fsk8s-preview-dark.png";
import Fsk8sPreviewLight from "../../../../assets/fsk8s/fsk8s-preview-light.png";
import perfectScaleOverview from "../../../../assets/fsk8s/perfectscale-overview.png";
import gcpLogo from "../../../../assets/gcp-logo.svg";
import { useDarkThemeCheck } from "../../../../Components/hooks/useDarkThemeCheck";
import { useIsFeatureEntitled } from "../../../../Context/TierProvider";
import { UpgradeOptions } from "../../../Upsell/UpgradeOptions";
import { useTrackKubernetesLensEvent } from "../../events";
import PerfectScaleLink from "../Components/PerfectScaleLink";
import { type K8sUpsellMode } from "../Fsk8sUpsell";

const overviewBulletPoints: Record<K8sUpsellMode, Record<string, string>> = {
  perfectscale: {
    "FinOps automation": "Cut Kubernetes costs by up to 50%",
    "AI-driven insights": "Get insights on usage patterns",
    "DevOps excellence": "Gain optimal performance and reliability",
  },
  lens: {
    "Cost visibility": "Track and monitor your Kubernetes spend",
    "Resource monitoring": "Track CPU and memory utilization",
    "Workload analysis": "Explore deployment performance",
    "Container-level view": "Analyze individual resource use",
  },
};

interface OverviewProps {
  mode: K8sUpsellMode;
}

const Overview = ({ mode }: OverviewProps) => {
  const theme = useTheme();
  const isDarkTheme = useDarkThemeCheck();
  const customerHasEntitlement = useIsFeatureEntitled("lens:k8s");
  const trackKubernetesLensEvent = useTrackKubernetesLensEvent();

  const [showUpgradeOptions, setShowUpgradeOptions] = useState(false);

  const handleUpgradeOptionsClick = () => {
    setShowUpgradeOptions(true);

    if (mode === "lens") {
      trackKubernetesLensEvent("Upgrade Options Button Clicked", {});
    }
  };

  const getImageSrc = () => {
    if (mode === "lens") {
      return isDarkTheme ? Fsk8sPreviewDark : Fsk8sPreviewLight;
    }

    return perfectScaleOverview;
  };

  const renderActions = () => {
    if (mode === "lens") {
      return (
        <>
          {!customerHasEntitlement && (
            <Button onClick={handleUpgradeOptionsClick} variant="contained" color="primary">
              View upgrade options
            </Button>
          )}
          <Button
            variant="text"
            href="https://help.doit.com/docs/kubernetes"
            startIcon={<OpenInNewIcon />}
            target="_blank"
          >
            Learn more
          </Button>
        </>
      );
    }

    // TODO: Return only this once we can know if a customer has PerfectScale or not
    // return <PerfectScaleLink />;

    return (
      <Stack direction="row" spacing={1}>
        <PerfectScaleLink />
        <Button
          href="https://app.perfectscale.io/account/login"
          target="_blank"
          startIcon={<OpenInNewIcon />}
          variant="outlined"
        >
          Log into PerfectScale
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <Grid container spacing={6}>
        <Grid
          size={{
            sm: 12,
            md: 6,
          }}
        >
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography variant="h1">
                {mode === "lens"
                  ? "Gain deep insights into your Kubernetes environment"
                  : "Put your Kubernetes optimization on auto-pilot, powered by PerfectScale for Kubernetes"}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  size="small"
                  icon={
                    <img
                      src={isDarkTheme ? awsLogoDark : awsLogo}
                      width={18}
                      alt="AWS Logo"
                      style={{ margin: "0 2px 0 8px" }}
                    />
                  }
                  label="EKS available"
                  sx={{ backgroundColor: theme.palette.general.backgroundDark, color: theme.palette.text.primary }}
                />
                <Chip
                  size="small"
                  icon={<img src={gcpLogo} width={18} alt="GCP Logo" style={{ margin: "0 0 0 8px" }} />}
                  label="GKE available"
                  sx={{ backgroundColor: theme.palette.general.backgroundDark, color: theme.palette.text.primary }}
                />
                {mode === "perfectscale" && (
                  <Chip
                    size="small"
                    icon={<img src={azureLogo} width={16} alt="Azure Logo" style={{ margin: "0 0 0 8px" }} />}
                    label="AKS available"
                    sx={{ backgroundColor: theme.palette.general.backgroundDark, color: theme.palette.text.primary }}
                  />
                )}
              </Stack>
            </Stack>
            <Typography variant="body1">
              {mode === "lens" ? (
                <span>
                  Gain deep insights into your Kubernetes environment with granular analytics in the areas that matter
                  most:
                </span>
              ) : (
                <span>
                  Ensure peak performance with data-driven, autonomous actions to optimize each layer of your K8s stack
                  for comprehensive cost efficiency.
                </span>
              )}
              <ul>
                {mode === "lens"
                  ? Object.entries(overviewBulletPoints.lens).map(([key, value]) => (
                      <li key={key}>
                        <span style={{ fontWeight: 500 }}>{key}:</span> {value}
                      </li>
                    ))
                  : Object.values(overviewBulletPoints.perfectscale).map((value, idx) => <li key={idx}>{value}</li>)}
              </ul>
              {mode === "perfectscale" && <span> Sign up below to get started with a 30-day free trial. </span>}
            </Typography>
            <Stack direction="row" spacing={2}>
              {renderActions()}
            </Stack>
          </Stack>
        </Grid>
        <Grid
          size={{
            sm: 12,
            md: 6,
          }}
        >
          <Card
            sx={{
              width: "100%",
              height: "fit-content",
              minHeight: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ...(mode === "lens"
                ? { borderRadius: "20px", boxShadow: 10 }
                : {
                    border: "none",
                  }),
            }}
          >
            <CardMedia
              component="img"
              image={getImageSrc()}
              alt="Kubernetes Lens Preview"
              sx={
                mode === "lens"
                  ? {
                      width: "100%",
                      height: "auto",
                    }
                  : {
                      minWidth: "50%",
                      maxHeight: "400px",
                      width: "auto",
                    }
              }
            />
          </Card>
        </Grid>
      </Grid>
      <UpgradeOptions
        featureName="Kubernetes Lens"
        open={showUpgradeOptions}
        onClose={() => {
          setShowUpgradeOptions(false);
        }}
        sendUpsellEvent={() => {
          trackKubernetesLensEvent("Upsell Notification Clicked", {});
        }}
      />
    </>
  );
};

export default Overview;
